/**
 * @file routes/_app/decisions/index.tsx
 * @description 驗證決定委員會列表頁（M3 新審議生命週期）。
 *
 * 新審議生命週期（委員書面審議/書面簽名模型）：
 * - 待安排 → 審議中 → 待總經理核定 → 已決（通過/不通過/延議）（初審不合格 → 已退回）
 * - 主任 / 組長（manager）：指派委員、排程審議；無法定人數 quorum 設定。
 * - 委員（decider）：被指派後書面審議並簽名（signedCount），全員簽完進「待總經理核定」。
 *
 * 功能：
 * - 使用 ListPageShell 展示所有驗證決定記錄（分頁）
 * - 搜尋 + 狀態篩選 + 方案篩選
 * - 審議進度（signedCount/committeeSize）
 * - 「待我簽」StatusPill：當前 decider 為本案委員且尚未簽名且狀態為「審議中」
 * - 操作欄按角色：manager 可「指派委員」、decider 可「前往審議」、所有人可看詳情頁
 *
 * 角色：所有登入角色可查看；decider 可書面審議；admin/manager 可排程 / 指派委員 / 最終核定
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { message } from '@/lib/antd-app';
import { Badge, Button, Checkbox, Col, Input, Row, Select, Table, Tag, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import {
  AlertTriangle,
  CheckCheck,
  CheckCircle,
  Clock,
  Eye,
  RotateCcw,
  Search,
  Undo2,
  UserCheck,
  Users,
  XCircle,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { userCan } from '@zhcb/shared';
import { apiClient } from '@/lib/api-client';
import { useAuthStore } from '@/lib/auth-context';
import { EmptyState, ListPageShell, PageMetaItem, StatusPill } from '@/components/shell';
import { color, font, text, weight } from '@/styles/tokens';

/** TanStack Router 路由定義 */
export const Route = createFileRoute('/_app/decisions/')({ component: DecisionsListPage });

// ─── 型別定義 ─────────────────────────────────────────────────────────────────

/**
 * 後端 GET /api/decisions 回傳的列表項（新審議生命週期形狀）。
 * 舊投票欄位（quorum/votedCount/approveCount/rejectCount/deferCount/viewerHasVoted）已移除。
 */
interface ApiDecisionItem {
  id: number;
  code: string;
  caseId: number;
  caseCode: string;
  /** 驗證方案（如「有機」、「產銷履歷(TAP)」、「友善」） */
  scheme: string;
  /** 後端原始生命週期狀態 */
  status: string;
  /** 決議方式（書面/會議等） */
  reviewMethod: string | null;
  scheduledAt: string | null;
  /** 到期日（ISO string），未排程時為 null */
  dueAt: string | null;
  /** 審議結論文字，終結前為 null */
  conclusion: string | null;
  /** 最終決定（通過/不通過/延議），終結前為 null */
  finalDecision: string | null;
  finalizedAt: string | null;
  createdAt: string;
  clientId: number | null;
  /** 客戶名稱（序87 新增列） */
  clientName: string | null;
  /** 稽核計劃編號（派案時生成；未派案為 null，序87） */
  planCode: string | null;
  /** 稽核類別＝案件驗證類型（初次/追蹤/展延…，序87） */
  verificationType: string | null;
  /** 稽核小組成員姓名（頓號分隔，序87） */
  auditTeamNames: string | null;
  /** 總部歸卷日期＝稽核文件包移交快照時間（序87） */
  dossierSnapshotAt: string | null;
  /** 已指派委員人數 */
  committeeSize: number;
  /** 已完成簽名的委員人數 */
  signedCount: number;
  /** 以當前檢視者視角：是否為本案委員、是否已完成簽名 */
  viewerIsMember: boolean;
  viewerHasSigned: boolean;
}

interface ApiDecisionList {
  items: ApiDecisionItem[];
  total: number;
}

/**
 * UI 展示狀態（新審議生命週期標籤，由後端原始 status 映射而來）。
 */
type DecisionStatus =
  | '待安排審議'
  | '審議中'
  | '待總經理核定'
  | '通過'
  | '不通過'
  | '延議'
  | '已退回';

/** UI 展示用決定記錄型別 */
interface DecisionRecord {
  id: string;
  decisionNo: string;
  caseCode: string;
  /** 後端原始狀態（用於 API 篩選參數） */
  rawStatus: string;
  /** 映射後的 UI 展示狀態 */
  status: DecisionStatus;
  scheme: string;
  reviewMethod: string | null;
  conclusion: string | null;
  dueAt: string | null;
  finalDecision: string | null;
  finalizedAt: string | null;
  committeeSize: number;
  signedCount: number;
  viewerIsMember: boolean;
  viewerHasSigned: boolean;
  /** 序87 新增列 */
  clientName: string | null;
  planCode: string | null;
  verificationType: string | null;
  auditTeamNames: string | null;
  dossierSnapshotAt: string | null;
}

// ─── 狀態映射 ─────────────────────────────────────────────────────────────────

/** 後端原始狀態 → UI 展示標籤（新生命週期） */
function mapApiStatus(apiStatus: string): DecisionStatus {
  const map: Record<string, DecisionStatus> = {
    待安排: '待安排審議',
    審議中: '審議中',
    待總經理核定: '待總經理核定',
    已決_通過: '通過',
    已決_不通過: '不通過',
    已決_延議: '延議',
    已退回: '已退回',
  };
  return map[apiStatus] ?? '待安排審議';
}

/** 後端 API 列表項 → UI 展示型別 */
function mapApiToDecisionRecord(item: ApiDecisionItem): DecisionRecord {
  return {
    id: String(item.id),
    decisionNo: item.code,
    caseCode: item.caseCode,
    rawStatus: item.status,
    status: mapApiStatus(item.status),
    /** 將後端原始 pgEnum 值（TOP/TAP/ECO）映射為顯示標籤；無值時顯示 '—' */
    scheme: SCHEME_LABEL[item.scheme] ?? '—',
    reviewMethod: item.reviewMethod,
    conclusion: item.conclusion,
    dueAt: item.dueAt,
    finalDecision: item.finalDecision,
    finalizedAt: item.finalizedAt,
    committeeSize: item.committeeSize,
    signedCount: item.signedCount,
    viewerIsMember: item.viewerIsMember,
    viewerHasSigned: item.viewerHasSigned,
    clientName: item.clientName,
    planCode: item.planCode,
    verificationType: item.verificationType,
    auditTeamNames: item.auditTeamNames,
    dossierSnapshotAt: item.dossierSnapshotAt,
  };
}

// ─── 方案映射 ─────────────────────────────────────────────────────────────────

/**
 * 後端 pgEnum 原始值 → 前端顯示標籤（對齊專案慣例）。
 * TOP = 有機驗證、TAP = 產銷履歷、ECO = 友善農業。
 */
const SCHEME_LABEL: Record<string, string> = {
  TOP: '有機',
  TAP: '產銷履歷',
  ECO: '友善',
};

// ─── 常量 ─────────────────────────────────────────────────────────────────────

/** 決定狀態 → StatusPill 配置 */
const STATUS_PILL_MAP: Record<
  DecisionStatus,
  {
    tone: 'warning' | 'info' | 'success' | 'danger' | 'muted';
    dot: boolean;
    icon?: React.ReactNode;
  }
> = {
  待安排審議: { tone: 'warning', dot: true, icon: <Clock size={11} /> },
  審議中: { tone: 'info', dot: true, icon: <UserCheck size={11} /> },
  待總經理核定: { tone: 'warning', dot: true, icon: <CheckCheck size={11} /> },
  通過: { tone: 'success', dot: false, icon: <CheckCircle size={11} /> },
  不通過: { tone: 'danger', dot: false, icon: <XCircle size={11} /> },
  延議: { tone: 'muted', dot: false, icon: <RotateCcw size={11} /> },
  已退回: { tone: 'danger', dot: true, icon: <Undo2 size={11} /> },
};

/** 方案顯示標籤 → StatusPill 色調（鍵與 SCHEME_LABEL 值對齊） */
const SCHEME_PILL_TONE: Record<string, 'success' | 'brand' | 'info'> = {
  有機: 'success',
  產銷履歷: 'brand',
  友善: 'info',
};

/** 判斷決定狀態是否已終結（通過/不通過/延議） */
function isFinalized(status: DecisionStatus): boolean {
  return status === '通過' || status === '不通過' || status === '延議';
}

// ─── 主頁面組件 ───────────────────────────────────────────────────────────────

/**
 * 驗證決定委員會列表頁面主組件。
 */
function DecisionsListPage() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user!);
  const isDecider = user.roles.includes('decider');
  /** 主委職能：可安排審議（decision.arrange = admin/manager/auditor 或被授權主委）。
   * 用能力判斷而非硬編 isManager，讓技術委員會主委（decider/auditor 兼主委）也能在列表發起審議安排。 */
  const canArrangeCap = userCan(user, 'decision.arrange');

  /** 列表資料 */
  const [decisions, setDecisions] = useState<DecisionRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const pageSize = 10;
  /** 篩選條件 */
  const [searchText, setSearchText] = useState('');
  const [filterStatus, setFilterStatus] = useState<DecisionStatus | undefined>(undefined);
  const [filterScheme, setFilterScheme] = useState<string | undefined>(undefined);
  /** Decider 用「我未簽署」快速過濾（後端篩選 status=審議中） */
  const [myUnsigned, setMyUnsigned] = useState(false);

  // ─── 資料拉取 ────────────────────────────────────────────────────

  const fetchDecisions = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        pageSize: String(pageSize),
      });
      // 「我未簽署」快速過濾：後端篩選審議中案件
      if (myUnsigned) params.set('status', '審議中');
      const data = await apiClient.get<ApiDecisionList>(`/api/decisions?${params.toString()}`);
      setDecisions(data.items.map(mapApiToDecisionRecord));
      setTotal(data.total);
    } catch (err) {
      console.error('[decisions-list] 拉取決定列表失敗:', err);
      message.error('無法載入決定列表，請稍後再試');
    } finally {
      setLoading(false);
    }
  }, [page, myUnsigned]);

  useEffect(() => {
    void fetchDecisions();
  }, [fetchDecisions]);

  // ─── 前端篩選 ─────────────────────────────────────────────────────

  const filteredDecisions = useMemo(() => {
    let result = decisions;
    if (searchText.trim()) {
      const k = searchText.trim().toLowerCase();
      result = result.filter(
        (d) => d.decisionNo.toLowerCase().includes(k) || d.caseCode.toLowerCase().includes(k),
      );
    }
    if (filterStatus) result = result.filter((d) => d.status === filterStatus);
    if (filterScheme) result = result.filter((d) => d.scheme === filterScheme);
    return result;
  }, [decisions, searchText, filterStatus, filterScheme]);

  // ─── 統計 ─────────────────────────────────────────────────────────

  /** 判斷當前 decider 是否「待我簽」：為本案委員、尚未簽名、審議進行中 */
  const isMyTurn = useCallback(
    (d: DecisionRecord) => d.viewerIsMember && !d.viewerHasSigned && d.status === '審議中',
    [],
  );

  const stats = useMemo(() => {
    const myTurn = isDecider ? decisions.filter(isMyTurn).length : 0;
    const inReview = decisions.filter((d) => d.status === '審議中').length;
    const pendingGm = decisions.filter((d) => d.status === '待總經理核定').length;
    const decided = decisions.filter((d) => isFinalized(d.status)).length;
    return { myTurn, inReview, pendingGm, decided };
  }, [decisions, isDecider, isMyTurn]);

  // ─── 事件處理 ────────────────────────────────────────────────────

  const goDetail = useCallback(
    (id: string) => void navigate({ to: '/decisions/$id', params: { id } }),
    [navigate],
  );

  const handleReset = () => {
    setSearchText('');
    setFilterStatus(undefined);
    setFilterScheme(undefined);
  };

  // ─── 表格欄位 ─────────────────────────────────────────────────────

  // 序87：欄位重排——審議狀態 / 審議進度 / 計劃編號 / 客戶名稱 / 總部歸卷日期 /
  // 驗證方案 / 稽核類別 / 審議方式 / 到期 / 結論 / 稽核小組
  const columns: ColumnsType<DecisionRecord> = [
    {
      title: '審議狀態',
      dataIndex: 'status',
      key: 'status',
      width: 150,
      render: (status: DecisionStatus, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          {/* 待我簽標記：當前 decider 為委員且尚未簽署 */}
          {isMyTurn(record) && (
            <StatusPill tone="danger" variant="solid" size="sm" dot>
              待我簽
            </StatusPill>
          )}
          <StatusPill
            tone={STATUS_PILL_MAP[status].tone}
            dot={STATUS_PILL_MAP[status].dot}
            icon={STATUS_PILL_MAP[status].icon}
            size="sm"
          >
            {status}
          </StatusPill>
        </div>
      ),
    },
    {
      title: '審議進度',
      key: 'progress',
      width: 90,
      render: (_, record) => {
        if (record.committeeSize === 0) {
          return <span style={{ color: color.text.muted, fontSize: text.sm }}>未指派委員</span>;
        }
        return (
          <span
            style={{
              fontSize: text.sm,
              fontFamily: font.mono,
              fontVariantNumeric: 'tabular-nums',
              color:
                record.signedCount >= record.committeeSize
                  ? color.state.success
                  : color.text.secondary,
            }}
          >
            {record.signedCount} / {record.committeeSize}
          </span>
        );
      },
    },
    {
      // 序87：決定編號改顯示「計劃編號」（派案時生成的稽核計劃編號）；未派案回落決定編號
      title: '計劃編號',
      key: 'planCode',
      width: 170,
      render: (_, record) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
          <span
            style={{
              fontFamily: font.mono,
              fontSize: text.sm,
              color: color.text.primary,
              fontWeight: weight.semibold,
            }}
          >
            {record.planCode ?? record.decisionNo}
          </span>
          <span style={{ fontFamily: font.mono, fontSize: text.xs, color: color.text.muted }}>
            {record.decisionNo}
          </span>
        </div>
      ),
    },
    {
      title: '客戶名稱',
      dataIndex: 'clientName',
      key: 'clientName',
      width: 150,
      ellipsis: true,
      render: (val: string | null) =>
        val ?? <span style={{ color: color.text.muted, fontSize: text.sm }}>—</span>,
    },
    {
      title: '總部歸卷日期',
      dataIndex: 'dossierSnapshotAt',
      key: 'dossierSnapshotAt',
      width: 120,
      render: (val: string | null) =>
        val ? (
          <span style={{ fontSize: text.sm, color: color.text.secondary }}>
            {dayjs(val).format('YYYY-MM-DD')}
          </span>
        ) : (
          <span style={{ color: color.text.muted, fontSize: text.sm }}>—</span>
        ),
    },
    {
      title: '驗證方案',
      dataIndex: 'scheme',
      key: 'scheme',
      width: 110,
      render: (scheme: string) => (
        <StatusPill tone={SCHEME_PILL_TONE[scheme] ?? 'neutral'} size="sm" variant="outline">
          {scheme}
        </StatusPill>
      ),
    },
    {
      title: '稽核類別',
      dataIndex: 'verificationType',
      key: 'verificationType',
      width: 110,
      ellipsis: true,
      render: (val: string | null) =>
        val ?? <span style={{ color: color.text.muted, fontSize: text.sm }}>—</span>,
    },
    {
      title: '審議方式',
      dataIndex: 'reviewMethod',
      key: 'reviewMethod',
      width: 100,
      render: (val: string | null) =>
        val ? (
          <span style={{ fontSize: text.sm, color: color.text.secondary }}>{val}</span>
        ) : (
          <span style={{ color: color.text.muted, fontSize: text.sm }}>—</span>
        ),
    },
    {
      title: '到期',
      key: 'dueAt',
      width: 110,
      render: (_, record) => {
        // 已終結：顯示終結日期
        if (isFinalized(record.status)) {
          return record.finalizedAt ? (
            <span style={{ fontSize: text.sm, color: color.text.muted }}>
              {dayjs(record.finalizedAt).format('YYYY-MM-DD')}
            </span>
          ) : (
            <span style={{ color: color.text.muted, fontSize: text.sm }}>—</span>
          );
        }
        // 未設到期日
        if (!record.dueAt) {
          return <span style={{ color: color.text.muted, fontSize: text.sm }}>—</span>;
        }
        const daysLeft = dayjs(record.dueAt).diff(dayjs(), 'day');
        if (daysLeft < 0) {
          // 已超過到期日
          return (
            <Tag color="red" style={{ marginInlineEnd: 0 }}>
              已逾期
            </Tag>
          );
        }
        return (
          <span
            style={{
              fontSize: text.sm,
              color: daysLeft <= 3 ? color.state.warning : color.text.secondary,
              fontWeight: daysLeft <= 3 ? weight.semibold : weight.medium,
            }}
          >
            剩 {daysLeft} 天
          </span>
        );
      },
    },
    {
      title: '結論',
      dataIndex: 'conclusion',
      key: 'conclusion',
      width: 120,
      ellipsis: true,
      render: (val: string | null) =>
        val ? (
          <span style={{ fontSize: text.sm, color: color.text.secondary }}>{val}</span>
        ) : (
          <span style={{ color: color.text.muted, fontSize: text.sm }}>—</span>
        ),
    },
    {
      title: '稽核小組',
      dataIndex: 'auditTeamNames',
      key: 'auditTeamNames',
      width: 150,
      ellipsis: true,
      render: (val: string | null) =>
        val ?? <span style={{ color: color.text.muted, fontSize: text.sm }}>—</span>,
    },
    {
      title: '操作',
      key: 'action',
      width: 200,
      fixed: 'right',
      render: (_, record) => {
        const notFinalized = !isFinalized(record.status);
        // 主委/管理組：尚未指派委員時可前往審議安排（能力判斷，涵蓋技術委員會主委）
        const canAssign = canArrangeCap && notFinalized && record.committeeSize === 0;
        // decider：為本案委員、未簽署、審議中可前往書面審議
        const canSign =
          isDecider &&
          record.viewerIsMember &&
          !record.viewerHasSigned &&
          record.status === '審議中';
        return (
          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            <Tooltip title="檢視詳情">
              <Button
                type="text"
                size="small"
                icon={<Eye size={14} />}
                onClick={() => goDetail(record.id)}
              />
            </Tooltip>
            {canAssign && (
              <Button
                type="primary"
                size="small"
                icon={<Users size={13} />}
                onClick={() => goDetail(record.id)}
              >
                審議安排
              </Button>
            )}
            {canSign && (
              <Button
                type="primary"
                size="small"
                icon={<UserCheck size={13} />}
                onClick={() => goDetail(record.id)}
              >
                前往審議
              </Button>
            )}
            {!canAssign && !canSign && (
              <Button type="text" size="small" onClick={() => goDetail(record.id)}>
                詳情頁
              </Button>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <ListPageShell
      title="驗證決定委員會"
      description="主任 / 組長指派委員並排程審議；委員（decider）書面審議並簽名，全員簽完後由總經理核定。系統標記「待我簽」項目以提示委員優先處理。"
      meta={
        <>
          {/* 待我簽：僅 decider 有意義 */}
          <PageMetaItem
            icon={<AlertTriangle size={13} />}
            label="待我簽"
            value={
              <span
                style={{
                  color: stats.myTurn > 0 ? color.state.danger : color.text.primary,
                  fontWeight: stats.myTurn > 0 ? weight.bold : weight.medium,
                }}
              >
                {stats.myTurn} 件
              </span>
            }
          />
          <PageMetaItem
            icon={<UserCheck size={13} />}
            label="審議中"
            value={`${stats.inReview} 件`}
          />
          <PageMetaItem
            icon={<Clock size={13} />}
            label="待總經理核定"
            value={`${stats.pendingGm} 件`}
          />
          <PageMetaItem
            icon={<CheckCheck size={13} />}
            label="已決"
            value={`${stats.decided} 件`}
          />
        </>
      }
      actions={undefined}
      filters={
        <Row gutter={[12, 8]} style={{ width: '100%' }} align="middle">
          <Col xs={24} sm={12} md={8}>
            <Input
              placeholder="搜尋決定編號 / 案件編號"
              prefix={<Search size={14} color={color.text.muted} />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={12} sm={6} md={4}>
            {/* 狀態篩選（前端過濾，使用 UI 展示標籤值） */}
            <Select<DecisionStatus>
              placeholder="審議狀態"
              options={(Object.keys(STATUS_PILL_MAP) as DecisionStatus[]).map((s) => ({
                label: s,
                value: s,
              }))}
              value={filterStatus}
              onChange={setFilterStatus}
              allowClear
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={12} sm={6} md={4}>
            {/* option value 與 mapApiToDecisionRecord 轉換後的 scheme 標籤一致，才能前端過濾命中 */}
            <Select
              placeholder="驗證方案"
              options={[
                { label: '有機', value: '有機' },
                { label: '產銷履歷', value: '產銷履歷' },
                { label: '友善', value: '友善' },
              ]}
              value={filterScheme}
              onChange={setFilterScheme}
              allowClear
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} md={4}>
            <Button
              type="text"
              onClick={handleReset}
              disabled={!searchText && !filterStatus && !filterScheme}
            >
              重置篩選
            </Button>
          </Col>
        </Row>
      }
      toolbar={
        <>
          <span style={{ fontSize: text.sm, color: color.text.tertiary }}>
            找到 <strong style={{ color: color.text.primary }}>{filteredDecisions.length}</strong>{' '}
            筆 / 共 <strong style={{ color: color.text.primary }}>{total}</strong> 筆
          </span>
          {/* 僅 decider 顯示「我未簽署」快速篩選 */}
          {isDecider && (
            <Checkbox
              checked={myUnsigned}
              onChange={(e) => {
                setMyUnsigned(e.target.checked);
                setPage(1);
              }}
            >
              <Badge dot={myUnsigned}>我未簽署</Badge>
            </Checkbox>
          )}
        </>
      }
    >
      {!loading && filteredDecisions.length === 0 ? (
        <EmptyState
          variant="empty"
          title="尚無符合條件的驗證決定"
          description="試著調整搜尋關鍵字、狀態或方案篩選條件。"
          size="md"
        />
      ) : (
        <Table<DecisionRecord>
          columns={columns}
          dataSource={filteredDecisions}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1200 }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: false,
            showTotal: (t) => `共 ${t} 筆資料`,
            onChange: (p) => setPage(p),
          }}
          size="middle"
          onRow={(record) => ({
            onClick: () => goDetail(record.id),
            style: { cursor: 'pointer' },
          })}
        />
      )}
    </ListPageShell>
  );
}
